// /src/components/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import api from '../api/axios';
import { 
  FaBuilding, 
  FaUsers, 
  FaComments, 
  FaQrcode,
  FaChartLine,
  FaCalendar,
  FaSpinner,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, loading }) => {
  const { t } = useTranslation();
  
  return (
    <Card className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-all duration-300">
      <CardContent className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <FaSpinner className="animate-spin text-gray-500 text-2xl" />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">{title}</p>
              <h3 className="text-2xl font-bold text-white mt-2">{value}</h3>
              {trend && (
                <p className={`text-sm ${trendValue >= 0 ? 'text-green-400' : 'text-red-400'} mt-1`}>
                  {t('dashboard.metrics.trends.increase', { value: Math.abs(trendValue) })}
                </p>
              )}
            </div>
            <div className="p-4 bg-indigo-500 bg-opacity-20 rounded-lg">
              <Icon className="h-6 w-6 text-indigo-400" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const QuickActionCard = ({ title, description, icon: Icon, to }) => (
  <Link to={to}>
    <Card className="bg-gray-800 border-gray-700 hover:border-indigo-500/50 transition-all duration-300">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-gray-700 rounded-lg">
            <Icon className="h-6 w-6 text-indigo-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="mt-1 text-sm text-gray-400">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    properties: 0,
    activeGuests: 0,
    totalRevenue: 0,
    conversations: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/stats', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    {
      title: t('dashboard.quickActions.manageProperties.title'),
      description: t('dashboard.quickActions.manageProperties.description'),
      icon: FaBuilding,
      to: '/properties'
    },
    {
      title: t('dashboard.quickActions.chatInterface.title'),
      description: t('dashboard.quickActions.chatInterface.description'),
      icon: FaComments,
      to: '/chat'
    },
    {
      title: t('dashboard.quickActions.qrManagement.title'),
      description: t('dashboard.quickActions.qrManagement.description'),
      icon: FaQrcode,
      to: '/qrcodes'
    }
  ];

  return (
    <div className="flex-1 space-y-8 p-8 pt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-white">{t('dashboard.title')}</h2>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.stats.totalProperties.title')}
          value={stats.properties}
          icon={FaBuilding}
          trend
          trendValue={12}
          loading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.activeGuests.title')}
          value={stats.activeGuests}
          icon={FaUsers}
          trend
          trendValue={-5}
          loading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.totalRevenue.title')}
          value={`${t('dashboard.stats.totalRevenue.prefix')}${stats.totalRevenue.toLocaleString()}`}
          icon={FaChartLine}
          trend
          trendValue={23}
          loading={isLoading}
        />
        <StatCard
          title={t('dashboard.stats.activeConversations.title')}
          value={stats.conversations}
          icon={FaComments}
          trend
          trendValue={8}
          loading={isLoading}
        />
      </div>

      {/* Quick Actions */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-white">
          {t('dashboard.quickActions.title')}
        </h3>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action, index) => (
            <QuickActionCard
              key={index}
              title={action.title}
              description={action.description}
              icon={action.icon}
              to={action.to}
            />
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-white">
          {t('dashboard.recentActivity.title')}
        </h3>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <FaSpinner className="animate-spin text-gray-500 text-2xl" />
                <span className="ml-2 text-gray-400">{t('dashboard.loading.activity')}</span>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-400">{t('dashboard.recentActivity.noActivity')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;